import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useIntl } from "../../../../_metronic/i18n/customUseIntl";
import { useSubheader } from "../../../../_metronic/layout";
import RoiCalculatorForm from "../../components/FarmFlow/RoiCalculator/RoiCalculatorForm";
import {
  NotificationDialog,
  NotificationDialogType,
} from "../../components/helpers/NotificationDialog";
import { useRoiCalculationData } from "../../hooks/useRoiCalculationData";
import { useRoiCalculationSnapshot } from "../../hooks/useRoiCalculationSnapshot";
import { toRoiFormInputs } from "../../helpers/roiOutMapper";
import { fromRoiFormInputs, parseRoiFormInputs } from "../../helpers/roiInMapper";
import { persistInterventionRoiCalculation, extractGraphqlErrorMessage } from "../../apicalls/roiFunctions";
import { RoiCalculatorFormData } from "../../models/RoiCalculator";

interface RoiCalculatorRouteParams {
  customerID: string;
  farmID: string;
  processID: string;
  interventionId: string;
}

interface RoiCalculatorLocationState {
  baselineName?: string;
  interventionName?: string;
  formData?: RoiCalculatorFormData | null;
  farmName?: string;
  processName?: string;
  animalType?: string;
}

const SustellRoiCalculatorPage = () => {
  const history = useHistory();
  const intl = useIntl();
  const location = useLocation<RoiCalculatorLocationState | undefined>();
  const { customerID, farmID, processID, interventionId } =
    useParams<RoiCalculatorRouteParams>();
  const subheader = useSubheader();

  const {
    baselineName: baselineNameFromState,
    interventionName: interventionNameFromState,
    formData: savedFormDataFromState = null,
    farmName,
    processName,
    animalType,
  } = location.state || {};

  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    undefined
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    data: parsedData,
    formData,
    loading,
    failed,
  } = useRoiCalculationData(farmID, interventionId, selectedCurrency);

  const {
    calculation,
    loading: calculationLoading,
    failed: calculationFailed,
  } = useRoiCalculationSnapshot({
    customerId: customerID,
    baselineReference: parsedData?.baseline.reference,
    interventionReference: interventionId,
  });

  const readOnly = calculation !== null;

  const baselineName =
    parsedData?.baseline.name ?? baselineNameFromState ?? "Baseline";

  const interventionName =
    parsedData?.intervention.name ?? interventionNameFromState ?? "Intervention";

  const savedFormData = useMemo(() => {
    if (savedFormDataFromState) return savedFormDataFromState;
    const inputs = parseRoiFormInputs(calculation?.inputs);
    return inputs ? fromRoiFormInputs(inputs, formData) : null;
  }, [savedFormDataFromState, calculation, formData]);

  useEffect(() => {
    if (!farmName && !processName) return;
    const crumbs: {
      pathname?: string;
      title: string;
      state?: Record<string, unknown>;
    }[] = [
      {
        pathname: `/customers/${customerID}/myfarms`,
        title: intl.formatMessage({ id: "FARMS.CARD.TITLE" }),
        state: { animalType },
      },
    ];
    if (farmName) {
      crumbs.push({
        pathname: `/customers/${customerID}/farm/${farmID}`,
        title: farmName,
        state: { animalType },
      });
    }
    if (processName) {
      crumbs.push({
        pathname: `/customers/${customerID}/farm/${farmID}/pp/${processID}`,
        title: processName,
        state: { animalType },
      });
    }
    if (readOnly) {
      crumbs.push({
        pathname: `/customers/${customerID}/farm/${farmID}/pp/${processID}/roi/${interventionId}/results`,
        title: intl.formatMessage({ id: "SUSTELL.ROI.RESULTS.TITLE" }),
        state: {
          formData: savedFormData,
          farmName,
          processName,
          animalType,
        },
      });
      crumbs.push({
        title: intl.formatMessage({
          id: "SUSTELL.ROI.RESULTS.INPUT_VALUES_TITLE",
        }),
      });
    } else {
      crumbs.push({
        title: intl.formatMessage({ id: "SUSTELL.ROI.BREADCRUMB.CALCULATE" }),
      });
    }
    subheader.setBreadcrumbs(crumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [farmName, processName, readOnly, savedFormData]);

  const handleCancel = () => {
    history.push(`/customers/${customerID}/farm/${farmID}/pp/${processID}`);
  };

  const handleSubmit = async (submittedFormData: RoiCalculatorFormData) => {
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const inputs = toRoiFormInputs(submittedFormData, formData);
      const persisted = await persistInterventionRoiCalculation({
        customerId: customerID,
        farmId: farmID,
        animalSystemId: processID,
        oldName: interventionName,
        inputs,
      });

      if (!persisted) {
        throw new Error("persistInterventionRoi returned no calculation");
      }

      history.push(
        `/customers/${customerID}/farm/${farmID}/pp/${processID}/roi/${interventionId}/results`,
        { formData: submittedFormData, farmName, processName, animalType }
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("persistInterventionRoi failed:", extractGraphqlErrorMessage(err) ?? err);
      setSubmitError(intl.formatMessage({ id: "SUSTELL.ROI.SAVE_ERROR" }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading || calculationLoading) {
    return null;
  }

  if (failed || calculationFailed) {
    return <div>{intl.formatMessage({ id: "SUSTELL.ROI.LOAD_ERROR" })}</div>;
  }

  return (
    <>
      <NotificationDialog
        open={!!submitError}
        message={submitError}
        type={NotificationDialogType.ERROR}
        closeNotificationDialog={() => setSubmitError(null)}
      />
      <RoiCalculatorForm
        formData={formData}
        baselineName={baselineName}
        interventionName={interventionName}
        onCurrencyChange={setSelectedCurrency}
        onCancel={handleCancel}
        onSubmit={(data) => {
          // eslint-disable-next-line no-void
          void handleSubmit(data);
        }}
        readOnly={readOnly}
        savedFormData={savedFormData}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default SustellRoiCalculatorPage;
